import { memo } from "react";

import { Typography } from "antd";
import { DEFAULT_POINT_SIZE } from "constants/chart.constants";
import { useChartsContext } from "contexts/ChartContext";
import {
  DEFAULT_WIDTH,
  HIGHLIGHTED_WIDTH
} from "entities/charts/eva/translators/constants";

import { useChartFeatureHandlers } from "hooks";
import { useChartEntities } from "hooks/charts";

import { LabeledSwitch, Tooltip } from "components/base";

import {
  Container,
  StyledDivider,
  StyledIcon,
  StyledSpace
} from "../shared/StyledComponents";
import SeriesStyleInput from "./SeriesStyleInput";
import { useLineSizeInteraction } from "./useLineSizeInteraction";

const { Text } = Typography;

const SeriesStylesOptions = () => {
  const { id } = useChartsContext();
  const { handleNumericChange, handleToggle } = useChartFeatureHandlers();
  const {
    pointSize,
    pointOpacity,
    lineSize,
    lineOpacity,
    highlightedLineSize,
    highlightedLineOpacity,
    useSeriesStyleOverride
  } = useChartEntities(id);

  const {
    handleLineSizeChange,
    handleHighlightedLineSizeChange,
    handleHighlightedLineSizeReset,
    shouldShowHighlightedLineSizeReset
  } = useLineSizeInteraction();

  const showPointSettings =
    pointSize?.show && pointOpacity?.show && useSeriesStyleOverride?.active;
  const showLineSettings =
    lineSize?.show && lineOpacity?.show && useSeriesStyleOverride?.active;
  const showHighlightedLineSettings =
    highlightedLineSize?.show &&
    highlightedLineOpacity?.show &&
    useSeriesStyleOverride?.active;

  return (
    <StyledSpace direction="vertical" split={<StyledDivider />}>
      {useSeriesStyleOverride?.show && (
        <LabeledSwitch
          testId="useSeriesStyleOverrideToggle"
          switch={{
            isChecked: useSeriesStyleOverride?.active ?? false,
            onChange: (checked) => {
              handleToggle("useSeriesStyleOverride")(checked);
            }
          }}
          label={{
            value: "Series Style Override",
            isBold: true,
            isUppercase: true,
            tooltip:
              "When enabled, series style thickness settings override color palette thickness values"
          }}
        />
      )}
      {showPointSettings && (
        <>
          <Container>
            <Text>Point Size</Text>
            <Tooltip title="Size of points (1-40)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={pointSize?.properties?.value}
              defaultValue={DEFAULT_POINT_SIZE}
              min={0}
              max={40}
              data-testid="pointSizeInput"
              onChange={(val: number | null) => {
                if (val === null) {
                  return;
                }
                handleNumericChange("pointSize")(val);
              }}
            />
          </Container>
          <Container>
            <Text>Point Opacity (%)</Text>
            <Tooltip title="Transparency of points (0-100%)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={Math.round((pointOpacity?.properties?.value ?? 1) * 100)}
              defaultValue={100}
              min={0}
              max={100}
              data-testid="pointOpacityInput"
              onChange={(val: number | null) => {
                if (val === null) {
                  return;
                }
                handleNumericChange("pointOpacity")(val / 100);
              }}
            />
          </Container>
        </>
      )}

      {showLineSettings && (
        <>
          <Container>
            <Text>Line Size</Text>
            <Tooltip title="Thickness of lines (1-20)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={lineSize?.properties?.value}
              defaultValue={DEFAULT_WIDTH}
              min={1}
              max={20}
              data-testid="lineSizeInput"
              onChange={handleLineSizeChange}
            />
          </Container>
          <Container>
            <Text>Line Opacity (%)</Text>
            <Tooltip title="Transparency of lines (0-100%)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={Math.round((lineOpacity?.properties?.value ?? 1) * 100)}
              defaultValue={100}
              min={0}
              max={100}
              data-testid="lineOpacityInput"
              onChange={(val: number | null) => {
                if (val === null) {
                  return;
                }
                handleNumericChange("lineOpacity")(val / 100);
              }}
            />
          </Container>
        </>
      )}

      {showHighlightedLineSettings && (
        <>
          <Container>
            <Text>Highlighted Line Size</Text>
            <Tooltip title="Thickness of highlighted lines on hover (1-40)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={highlightedLineSize?.properties?.value}
              defaultValue={HIGHLIGHTED_WIDTH}
              min={1}
              max={40}
              data-testid="highlightedLineSizeInput"
              onChange={handleHighlightedLineSizeChange}
              onReset={handleHighlightedLineSizeReset}
              showResetButton={shouldShowHighlightedLineSizeReset}
            />
          </Container>
          <Container>
            <Text>Highlighted Line Opacity (%)</Text>
            <Tooltip title="Transparency of highlighted lines on hover (0-100%)">
              <StyledIcon />
            </Tooltip>
            <SeriesStyleInput
              value={Math.round((highlightedLineOpacity?.properties?.value ?? 1) * 100)}
              defaultValue={100}
              min={0}
              max={100}
              data-testid="highlightedLineOpacityInput"
              onChange={(val: number | null) => {
                if (val === null) {
                  return;
                }
                handleNumericChange("highlightedLineOpacity")(val / 100);
              }}
            />
          </Container>
        </>
      )}
    </StyledSpace>
  );
};

export default memo(SeriesStylesOptions);
